import {
  AssessmentEntry,
  AssessmentEntryComment,
  AssessmentMethodComment,
  AssessmentMethodGrade,
  AssessmentMethodScore,
  AssessmentMethodType,
  AssessmentsGrade,
  AssessmentWithEntries,
  FilterKeys,
  isAssessmentEntryComment,
  isAssessmentMethodComment,
} from '@schooly/api';
import { AssessmentsArrangeBy } from '@schooly/components/filters';
import { usePrevious } from '@schooly/hooks/use-previous';
import { GridRowDate, GridRowItem } from '@schooly/style';
import { CommentRowCell } from '@schooly/style';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { CommentController } from '../AssessmentEntryMethods/methods/CommentController';
import { GridRowStyled } from '../Grid/Grid';
import { ListViewAssessmentEntriesExtraRow } from './ListViewAssessmentEntriesRow.styled';
import { ListViewAssessmentsRowContentEntries } from './ListViewAssessmentsEntriesRowContentEntries';
import { AssessmentEntriesRowContentInfo } from './ListViewAssessmentsEntriesRowContentInfo';

const SET_ENTRIES_TIMEOUT = 300;

export interface AssessmentsEntriesRowProps {
  assessment: AssessmentWithEntries;
  lists?: Record<string, AssessmentsGrade>;
  relationId: string;
  showAllComments?: boolean;
  wideComments?: boolean;
  isCommentsOpen?: boolean;
  onOpenComments?: () => void;
  onCloseComments?: () => void;
  fullView: boolean;
  arrangeBy?: AssessmentsArrangeBy;
}

export const ListViewAssessmentEntriesRow: FC<AssessmentsEntriesRowProps> = ({
  assessment,
  lists,
  relationId,
  showAllComments,
  wideComments,
  isCommentsOpen,
  onOpenComments,
  onCloseComments,
  fullView,
  arrangeBy = FilterKeys.Date,
}) => {
  const prevShowAllComments = usePrevious(showAllComments);

  const assessmentWithEntries = useMemo(() => {
    return {
      ...assessment,
      // There all 3 methods should be presented
      methods: [
        {
          ...assessment.methods.find((method) => method.method_type === AssessmentMethodType.Score),
          method_type: AssessmentMethodType.Score,
        } as AssessmentMethodScore,
        {
          ...assessment.methods.find((method) => method.method_type === AssessmentMethodType.Grade),
          method_type: AssessmentMethodType.Grade,
        } as AssessmentMethodGrade,
        {
          ...assessment.methods.find(
            (method) => method.method_type === AssessmentMethodType.Comment,
          ),
          method_type: AssessmentMethodType.Comment,
        } as AssessmentMethodComment,
      ],
    };
  }, [assessment]);

  const [commentMethod, commentEntries] = useMemo(() => {
    const students = assessmentWithEntries.groups[0]?.students ?? [];
    const relation = students.find((item) => item.relation_id === relationId);
    const commentMethod = assessmentWithEntries.methods.find(isAssessmentMethodComment);

    const isCommentEntry = (entry: AssessmentEntry): entry is AssessmentEntryComment =>
      isAssessmentEntryComment(entry) && commentMethod?.id === entry.method.id;

    const commentEntries = relation ? relation.entries.filter(isCommentEntry) : [];

    return [commentMethod, commentEntries];
  }, [assessmentWithEntries.groups, assessmentWithEntries.methods, relationId]);

  const [localEntries, setLocalEntries] = useState<AssessmentEntryComment[] | undefined>(
    commentEntries.filter((entry) => !!entry.comment),
  );

  const hasComments = Boolean(localEntries?.length);

  useEffect(() => {
    if (!prevShowAllComments && showAllComments && hasComments) {
      onOpenComments?.();
    }

    if (prevShowAllComments && !showAllComments) {
      onCloseComments?.();
    }
  }, [hasComments, onCloseComments, onOpenComments, prevShowAllComments, showAllComments]);

  return (
    <GridRowStyled>
      <GridRowItem
        sx={(theme) => ({
          height: theme.spacing(5.5),
          pr: 0,
        })}
        noVerticalPadding
      >
        <AssessmentEntriesRowContentInfo
          assessment={assessment}
          fullView={!!fullView}
          arrangeBy={arrangeBy}
        />

        <ListViewAssessmentsRowContentEntries
          assessment={assessmentWithEntries}
          lists={lists}
          relationId={relationId}
          wideComments={!!wideComments}
          groupId={assessment.groups[0]?.id ?? ''}
          commentMethod={commentMethod}
          isCommentsOpen={!!isCommentsOpen}
          onCloseComments={onCloseComments}
          onOpenComments={onOpenComments}
          localEntries={localEntries}
          hasComments={hasComments}
        />
      </GridRowItem>
      {wideComments && commentMethod && isCommentsOpen && (
        <ListViewAssessmentEntriesExtraRow>
          <GridRowDate />
          <CommentRowCell rowStyleProps={{ width: '100%', border: 'none' }}>
            {(onToggle, cellRef) => (
              <CommentController
                relationId={relationId}
                groupId={assessment.groups[0]?.id ?? ''}
                entries={localEntries}
                // https://treehive.atlassian.net/browse/TR-2119:
                //
                // Sync comments update emits further CommentController remount
                // what might loos some simultaneous pointer events
                //
                // Postpone the process to the macrotasks stack to yield synchronous pointer events first
                onSetLocalEntries={(entries) =>
                  setTimeout(() => setLocalEntries(entries), SET_ENTRIES_TIMEOUT)
                }
                method={commentMethod}
                assessmentId={assessmentWithEntries.id}
                enterable={assessmentWithEntries.enterable_and_my_group}
                showAllComments={showAllComments}
                plainCommentsListOnly
                popoverMargin={2}
                onToggle={onToggle}
                getParentRef={() => cellRef}
                withoutBorderPosition
              />
            )}
          </CommentRowCell>
        </ListViewAssessmentEntriesExtraRow>
      )}
    </GridRowStyled>
  );
};
